import { FC } from "react";
import {
  Button,
  Col,
  Input,
  LegacyForm,
  Row,
  Skeleton,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { useAppSelector, useAppTranslation } from "@/utils";
import { userApis } from "@payping/api";
import { URLS } from "@payping/utilities-shared/consts";
import { buildUrl } from "@payping/utilities-shared/url";
import { Trans } from "@payping/utils/localeUtils";
import { useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router";

interface BusinessAddFormType {
  username: string;
  businessName: string;
}

const BusinessAdd: FC = () => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<BusinessAddFormType>();
  const navigate = useNavigate();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);

  const [{ loading: loadingAdd }, handleAdd] = useMessagedAsyncFn(
    async (values: BusinessAddFormType) => {
      await userApis.registerWithRequestModelWithCancellationTokenPost({
        username: values.username,
        email: `${values.username}@payping.io`,
        phoneNumber: personalInfo?.phoneNumber,
        // @ts-expect-error missing type
        businessName: values.businessName,
      });
      navigate(DashboardRoutes.home.root.url);
    },
    [personalInfo?.phoneNumber, navigate],
    undefined,
    { useProgress: true },
  );

  const loading = loadingAdd;

  return (
    <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
      <Typography.Title level={4} className="tw:font-medium">
        {t("mainNav.business.addAccount")}
      </Typography.Title>
      <Typography.Text>
        <Trans
          i18nKey={"settings.business.addBusinessHelp"}
          components={{
            a: <a href={buildUrl(URLS.AUTH_URL, "/auth/number").toString()} target={"_blank"} rel="noreferrer" />,
          }}
        />
      </Typography.Text>
      <Row className={"tw:w-full"}>
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <LegacyForm<BusinessAddFormType> onFinish={handleAdd} form={form} layout="vertical">
            <LegacyForm.Item
              rules={[
                { required: true },
                {
                  pattern: /^[a-zA-Z0-9]+$/,
                  message: t("common.validations.onlyEnglishLetterNumerals"),
                },
              ]}
              name={"username"}
              label={t("common.username")}
            >
              {loading ? <Skeleton.Input /> : <Input />}
            </LegacyForm.Item>
            <LegacyForm.Item
              rules={[
                { required: true },
                {
                  pattern: /^[\u0600-\u06FF\s]+$/,
                  message: t("common.validations.onlyPersianLetters"),
                },
              ]}
              name={"businessName"}
              label={t("settings.business.businessName")}
            >
              {loading ? <Skeleton.Input /> : <Input />}
            </LegacyForm.Item>
            <Space>
              <Button type={"primary"} loading={loading} htmlType={"submit"}>
                {t("common.submit")}
              </Button>

              <Button variant={"outlined"} color="primary" onClick={() => navigate(-1)}>
                {t("common.return")}
              </Button>
            </Space>
          </LegacyForm>
        </Col>
      </Row>
    </Space>
  );
};

export default BusinessAdd;
